import { useState } from 'react';
import styled from 'styled-components';

import { FilledBadge } from 'components/shared/Badge';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { InputStyles } from 'components/shared/form/Input';
import StandardModal, { Actions, ColorThemeName } from 'components/shared/StandardModal';
import ThinSearch from 'components/shared/ThinSearch';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import Workflow, { WorkflowAction, WorkflowStatus } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import BadgeCheckIcon from 'svg/BadgeCheckIcon';
import BadgeXIcon from 'svg/BadgeXIcon';

import {
  ASSIGNED_GROUP_ID_VALUE as GROUP_VALUE,
  DENIAL_REASON_VALUE as REASON_VALUE,
  OUTCOME_ACCEPTED,
  OUTCOME_DENIED,
  recordedOutcome,
} from './constants';
import { FanoutCheckbox } from './FacilityDecisionPanel';
import { completionAttribution } from './FanoutPanel';

const NOTE_VALUE = 'central_intake_note';

type Reason = WorkflowAction['denialReasonOptions'][number];

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: Record<string, string>) => unknown;
  onAmend?: (actionId: string, outputs: Record<string, string>) => unknown;
  isCompleting: boolean;
}

// Central intake's decision in the lighter Inbound Referrals (CentralIntakeDecision): pick ONE of the client's
// branches and Accept, or Deny with the sender's reason: a denial names the branch too. The list is the Facility Decision
// panel's, narrowed to a single selection with one decision for the whole referral. The API offers the branches and
// the reasons on the step, and tells the sender; the internal note stays with the provider.
export default function CentralIntakeDecisionPanel({ action, workflow, onComplete, onAmend, isCompleting }: Props) {
  const { canEditAction } = useWorkflowPermissions();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [denying, setDenying] = useState(false);

  if (action.isCompleted) {
    return (
      <CentralIntakeDecisionResult action={action} workflow={workflow} onAmend={onAmend} isCompleting={isCompleting} />
    );
  }

  const editable = canEditAction(action, workflow);
  const options = action.branchOptions;
  const term = search.trim().toLowerCase();
  const visible = term ? options.filter((option) => option.name.toLowerCase().includes(term)) : options;

  return (
    <Panel data-testid='central-intake-decision-panel'>
      {options.length > 4 && (
        <Toolbar>
          <SearchWrap>
            <ThinSearch value={search} onChange={setSearch} placeholder='Search for Branch by Name' />
          </SearchWrap>
        </Toolbar>
      )}

      <RowList $scroll={visible.length > 4}>
        {visible.map((option) => (
          <Row key={option.id} data-testid='decision-branch-row'>
            <FanoutCheckbox
              checked={selectedId === option.id}
              disabled={!editable}
              onChange={() => setSelectedId((current) => (current === option.id ? '' : option.id))}
              data-testid={`decision-branch-${option.id}`}
            />
            <RowName>{option.name}</RowName>
          </Row>
        ))}
        {visible.length === 0 && <Empty>No branches to send to.</Empty>}
      </RowList>

      {editable && (
        <Footer>
          <Button
            variant='outline-danger'
            size='small'
            disabled={isCompleting || !selectedId}
            onClick={() => setDenying(true)}
            data-testid='decision-deny'>
            Deny
          </Button>
          <Button
            size='small'
            disabled={isCompleting || !selectedId}
            onClick={() => onComplete(action.id, { final_outcome: OUTCOME_ACCEPTED, [GROUP_VALUE]: selectedId })}
            data-testid='decision-accept'>
            Accept
          </Button>
        </Footer>
      )}

      {denying && (
        <DenyModal
          isCompleting={isCompleting}
          reasons={action.denialReasonOptions}
          onCancel={() => setDenying(false)}
          onConfirm={(reason, note) => {
            setDenying(false);
            onComplete(action.id, {
              final_outcome: OUTCOME_DENIED,
              [GROUP_VALUE]: selectedId,
              [REASON_VALUE]: reason,
              [NOTE_VALUE]: note,
            });
          }}
        />
      )}
    </Panel>
  );
}

// The answer: Accepted and where it was sent, or Denied and why, who decided, and intake's note. An accepted referral
// can still be changed to Denied, keeping its branch.
export function CentralIntakeDecisionResult({
  action,
  workflow,
  onAmend,
  isCompleting = false,
}: {
  action: WorkflowAction;
  workflow: Workflow;
  onAmend?: (actionId: string, outputs: Record<string, string>) => unknown;
  isCompleting?: boolean;
}) {
  const { canEditAction } = useWorkflowPermissions();
  const [denying, setDenying] = useState(false);
  const accepted = recordedOutcome(workflow) === OUTCOME_ACCEPTED;
  const branchId = workflow.valueFor(GROUP_VALUE)?.value;
  const branchName = [...workflow.groups, ...action.branchOptions].find((group) => group.id === branchId)?.name;
  const reasonValue = workflow.valueFor(REASON_VALUE)?.value;
  const reason = action.denialReasonOptions.find((option) => option.apiName === reasonValue)?.displayName;
  const note = workflow.valueFor(NOTE_VALUE)?.value;
  const attribution = completionAttribution(action);
  const canChangeToDenied =
    accepted &&
    !!onAmend &&
    canEditAction(action, workflow) &&
    workflow.status !== WorkflowStatus.CANCELED &&
    !workflow.isLocked;

  const summary = accepted
    ? `Sent to ${branchName ?? 'another branch'}`
    : `Denied${branchName ? ` at ${branchName}` : ''}${reason ? ` — ${reason}` : ''}`;

  return (
    <Result data-testid='central-intake-decision-result'>
      <FilledBadge color={accepted ? 'green' : 'red'} icon={accepted ? <BadgeCheckIcon /> : <BadgeXIcon />}>
        {accepted ? 'Accepted' : 'Denied'}
      </FilledBadge>
      <BodySmall data-testid='decision-result-summary'>{summary}</BodySmall>
      {!accepted && typeof note === 'string' && note && (
        <BodySmall data-testid='decision-result-note'>Internal note: {note}</BodySmall>
      )}
      {attribution && <Muted>{attribution}</Muted>}
      {canChangeToDenied && (
        <Button
          variant='outline-danger'
          size='small'
          disabled={isCompleting}
          onClick={() => setDenying(true)}
          data-testid='decision-change-to-denied'>
          Change to Denied
        </Button>
      )}
      {denying && onAmend && (
        <DenyModal
          isCompleting={isCompleting}
          reasons={action.denialReasonOptions}
          onCancel={() => setDenying(false)}
          onConfirm={(reasonName, noteText) => {
            setDenying(false);
            onAmend(action.id, { final_outcome: OUTCOME_DENIED, [REASON_VALUE]: reasonName, [NOTE_VALUE]: noteText });
          }}
        />
      )}
    </Result>
  );
}

function DenyModal({
  isCompleting,
  reasons,
  onCancel,
  onConfirm,
}: {
  isCompleting: boolean;
  reasons: Reason[];
  onCancel: () => void;
  onConfirm: (reason: string, note: string) => void;
}) {
  const [reason, setReason] = useState<Reason | null>(null);
  const [note, setNote] = useState('');

  return (
    <StandardModal title='Deny Referral' onCancel={onCancel} colorTheme={ColorThemeName.PrimaryRed}>
      <DenyForm>
        <div>
          <FieldLabel name='decision-denial-reason'>Denial Reason</FieldLabel>
          <Select<Reason>
            inputId='decision-denial-reason'
            placeholder='Select a reason'
            options={reasons}
            value={reason}
            onChange={setReason}
            getOptionValue={(option) => option.apiName}
            getOptionLabel={(option) => option.displayName}
          />
        </div>
        <div>
          <FieldLabel name='decision-note'>Internal note (optional)</FieldLabel>
          <NoteInput
            id='decision-note'
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder='Only your team sees this; the referring facility never does'
            data-testid='decision-note'
          />
        </div>
      </DenyForm>
      <Actions>
        <Button variant='ghost' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant='danger'
          disabled={isCompleting || !reason}
          onClick={() => reason && onConfirm(reason.apiName, note.trim())}
          data-testid='decision-deny-confirm'>
          Deny Referral
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
`;

const Toolbar = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${colors.black5};
`;

const SearchWrap = styled.div`
  max-width: 360px;
`;

const RowList = styled.div<{ $scroll: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $scroll }) =>
    $scroll &&
    `
      max-height: 296px;
      overflow-y: auto;
    `}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;

  & + & {
    border-top: 1px solid ${colors.black5};
  }
`;

const RowName = styled(BodySmallBold)`
  font-size: 14px;
`;

const Empty = styled(BodySmall)`
  padding: 14px 16px;
  color: ${colors.black50};
`;

const Footer = styled(ButtonGroup)`
  justify-content: flex-end;
  padding: 12px 16px;
  border-top: 1px solid ${colors.black5};
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;

// Not the shared ModalContent: that centers a confirm's copy, and this is a form.
const DenyForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 20px 0 24px;
`;

const NoteInput = styled.textarea`
  ${InputStyles}
  min-height: 96px;
  resize: vertical;
`;
